import {mergeDeepObj} from '../object/mergeDeepObj';

export namespace WithCallbacks {
  export type Callback<T> =
    | ((instance: T) => Promise<void>)
    | ((instance: T) => void);

  export type Callbacks<T> = Partial<
    Record<keyof T, WithCallbacks.Callback<T>>
  >;

  export type ClassMethod<T> = {
    [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never;
  }[keyof T];

  export interface Interface<T extends Interface<T>> {
    callbacks: Manager<T>;
  }

  export class Manager<T extends WithCallbacks.Interface<T>> {
    callbacks: WithCallbacks.Callbacks<T> = {};

    init(instance: T): void {
      const prototype = Object.getPrototypeOf(instance);
      const propertyNames = Object.getOwnPropertyNames(prototype);

      const methodNames: string[] = [];

      for (let i = 0; i < propertyNames.length; i++) {
        const name = propertyNames[i];

        if (name) {
          try {
            const descriptor = Object.getOwnPropertyDescriptor(prototype, name);

            /**
             * accessing prototype[name] should go after getter check,
             * because it can throw an error if the instance is not ready.
             * Anyway, this try-catch block handles this case, but it might lead to
             * unexpected behavior if the getter throws an error,
             * so first check is the property isn't getter of setter ( both are fns after all).
             */
            if (
              name !== 'constructor' &&
              !descriptor?.get &&
              !descriptor?.set &&
              typeof prototype[name] === 'function'
            ) {
              methodNames.push(name);
            }
          } catch (e) {
            if (
              e instanceof TypeError &&
              e.message.includes('Cannot read properties of undefined')
            ) {
              //eslint-disable-next-line no-console
              console.debug(
                'getter read error while instance is not ready. Skipping',
              );
            } else {
              throw e;
            }
          }
        }
      }

      methodNames.forEach(methodName => {
        instance[methodName] = this.proxify(instance[methodName]);
      });
    }

    getCallback(prop: keyof T) {
      return this.callbacks[prop];
    }

    setCallbacks(callbacks: WithCallbacks.Callbacks<T> = {}) {
      this.callbacks = mergeDeepObj(this.callbacks, callbacks);
    }

    private proxify<M extends (...args: any) => any>(method: M) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      return new Proxy(method, {
        async apply(target, thisArg, args) {
          const result = await target.apply(thisArg, args);
          const callback = self.getCallback(target.name as keyof T);

          callback?.(thisArg);

          return result;
        },
      });
    }
  }
}
